import { useEffect, useRef, useState } from 'react';

/**
 * Per-character gold gradient text with a staggered fade-and-rise entrance.
 *
 * How the original worked (and why this is not a plain CSS port):
 * `background-clip: text` on the parent would restart the gradient inside every
 * character box once each char is its own element. So the source measured each
 * character's offset after layout and gave it the FULL-width gradient shifted
 * left by that offset — reassembling one continuous gradient across the word.
 * That measurement is inherently imperative, so it stays in a layout effect.
 *
 * Differences from the original, all deliberate:
 *  - Characters are rendered in JSX, so the text is present in the prerendered
 *    HTML. The original built them at runtime by wiping `textContent`, which
 *    meant the styled text only existed after JS ran.
 *  - The split characters are `aria-hidden` with a single visually-hidden copy
 *    of the real string alongside, so screen readers announce "Opportunity"
 *    instead of spelling "O-p-p-o-r-t-u-n-i-t-y".
 *  - Honors `prefers-reduced-motion`: the gradient still applies, the
 *    per-character animation does not.
 */

const GRADIENT = [
  'radial-gradient(ellipse 60% 60% at var(--p1x) var(--p1y), #a8893a, transparent)',
  'radial-gradient(ellipse 70% 70% at var(--p2x) var(--p2y), #c9a84c, transparent)',
  'radial-gradient(ellipse 90% 90% at var(--p3x) var(--p3y), #dfc070, transparent)',
  'radial-gradient(ellipse 100% 100% at var(--p4x) var(--p4y), #f0d898, transparent)',
].join(', ');

export interface GradientTextProps {
  children: string;
  /** Seconds before the first character animates in. */
  startDelay?: number;
  /** Seconds between consecutive characters. */
  perCharDelay?: number;
  /**
   * When true the animation runs on mount (the hero headline). When false it
   * waits until the element scrolls into view, matching the original's
   * gradientObserver behavior for non-hero gradient words.
   */
  immediate?: boolean;
  as?: 'span' | 'em';
  className?: string;
}

export function GradientText({
  children,
  startDelay = 0,
  perCharDelay = 0.06,
  immediate = false,
  as: Tag = 'span',
  className = '',
}: GradientTextProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [active, setActive] = useState(immediate);

  // Wait for scroll-in unless this is the hero.
  useEffect(() => {
    if (immediate) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [immediate]);

  // Measure each character and shift the shared gradient to line up.
  useEffect(() => {
    if (!active) return;
    const el = ref.current;
    if (!el) return;

    const apply = () => {
      const parent = el.getBoundingClientRect();
      const chars = el.querySelectorAll<HTMLElement>('.char--gradient');
      chars.forEach((span) => {
        const offsetLeft = span.getBoundingClientRect().left - parent.left;
        span.style.backgroundImage = GRADIENT;
        span.style.backgroundSize = `${parent.width}px ${parent.height}px`;
        span.style.backgroundPosition = `-${offsetLeft}px 0`;
      });
      // Match original JS: clear parent background once per-character slicing is applied
      el.style.background = 'none';
    };

    const frame = requestAnimationFrame(apply);

    // Re-slice on resize: the text reflows and every offset changes.
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [active]);

  const words = children.split(' ');
  let charIndex = 0;
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <>
      {/* Real string for assistive tech — outside the text-clipped gradient container */}
      <span className="sr-only">{children}</span>
      <Tag
        ref={ref as never}
        className={`hero-gradient ${className}`.trim()}
        aria-hidden="true"
      >
        <span>
        {words.map((word, wordIdx) => {
          const wordSpan = (
            <span className="char-word" key={`w${wordIdx}`}>
              {[...word].map((char, i) => {
                const delay = startDelay + charIndex * perCharDelay;
                charIndex += 1;
                return (
                  <span
                    key={i}
                    className="char char--gradient"
                    style={
                      active && !reduced
                        ? { animationDelay: `${delay.toFixed(3)}s` }
                        : { animation: 'none', opacity: 1 }
                    }
                  >
                    {char}
                  </span>
                );
              })}
            </span>
          );
          if (wordIdx === words.length - 1) return wordSpan;
          const spaceDelay = startDelay + charIndex * perCharDelay;
          charIndex += 1;
          return (
            <span key={`g${wordIdx}`}>
              {wordSpan}
              <span
                className="char char--space"
                style={
                  active && !reduced
                    ? { animationDelay: `${spaceDelay.toFixed(3)}s` }
                    : { animation: 'none', opacity: 1 }
                }
              >
                {' '}
              </span>
            </span>
          );
        })}
        </span>
      </Tag>
    </>
  );
}

/**
 * Plain (non-gradient) staggered character reveal — the original's
 * `splitAndAnimate`, used on the contact and CTA headings.
 */
export function SplitText({
  children,
  startDelay = 0.1,
  perCharDelay = 0.03,
  className = '',
}: {
  children: string;
  startDelay?: number;
  perCharDelay?: number;
  className?: string;
}) {
  const ref = useRef<HTMLSpanElement | null>(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setActive(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActive(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const words = children.split(' ');
  let charIndex = 0;

  return (
    <>
      <span className="sr-only">{children}</span>
      <span ref={ref} className={className} aria-hidden="true">
        {words.map((word, wordIdx) => {
          const chars = [...word].map((char, i) => {
            const delay = startDelay + charIndex * perCharDelay;
            charIndex += 1;
            return (
              <span
                key={i}
                className="char"
                style={
                  active
                    ? { animationDelay: `${delay.toFixed(3)}s` }
                    : { animation: 'none', opacity: 1 }
                }
              >
                {char}
              </span>
            );
          });
          const isLast = wordIdx === words.length - 1;
          const spaceDelay = startDelay + charIndex * perCharDelay;
          if (!isLast) charIndex += 1;
          return (
            <span key={wordIdx}>
              <span className="char-word">{chars}</span>
              {!isLast && (
                <span
                  className="char char--space"
                  style={
                    active
                      ? { animationDelay: `${spaceDelay.toFixed(3)}s` }
                      : { animation: 'none', opacity: 1 }
                  }
                >
                  {' '}
                </span>
              )}
            </span>
          );
        })}
      </span>
    </>
  );
}
